/**
 * Component: Ajax Cart
 * ------------------------------------------------------------------------------
 * Create a mini cart with increase and decrease quantity function.
 *
 * @namespace ajaxCart
 */
import { formatMoney } from '@shopify/theme-currency';
import * as cart from '@shopify/theme-cart';
import axios from 'axios';
import cssClasses from '../helpers/cssClasses';
import {
  quantitySelector,
  quantitySelectorTemplate,
} from './quantity-selector';

/**
 * DOM selectors.
 */
const selectors = {
  container: '[js-cart="container"]',
  cart: '[js-cart="cart"]',
  cartHeader: '[js-ajax-cart="header"]',
  quantity: '[js-cart="quantity"]',
  quantitySelector: '[js-cart="quantitySelector"]',
  itemList: '[js-cart="list"]',
  totalPrice: '[js-cart="totalPrice"]',
  counter: '[js-ajax-cart="counter"]',
  lineItem: '[js-cart-table="lineItem"]',
};

/**
 * Export default AjaxCart.
 */
export default () => {
  /**
   * DOM node selectors.
   */
  const nodeSelectors = {
    container: document.querySelector(selectors.container),
    cart: document.querySelector(selectors.cart),
    cartHeader: document.querySelector(selectors.cartHeader),
    quantity: [...document.querySelectorAll(selectors.quantity)],
    totalPrice: [...document.querySelectorAll(selectors.totalPrice)],
    counter: [...document.querySelectorAll(selectors.counter)],
    itemList: document.querySelector(selectors.itemList),
    lineItem: document.querySelector(selectors.lineItem),
  };

  function init() {
    setBusListeners();
    setEventListeners();
  }

  function setBusListeners() {
    Karmoon.EventBus.listen(
      ['AddToCart:itemRemoved', 'AddToCart:itemAdded', 'Cart:itemAdded'],
      () => {
        getCartJson();
      }
    );

    Karmoon.EventBus.listen(['AddToCart:isRemoving', 'Cart:isUpdating'], () => {
      cartUpdating();
    });

    Karmoon.EventBus.listen('Cart:updated', (cartData) => {
      cartUpdated();
      rebuildCart(cartData);
    });
  }

  function setEventListeners() {
    getCartJson();
  }

  function rebuildCart(cartData) {
    cartRender(cartData);
    cartCounter(cartData);
    updateTotal(cartData);
  }

  /**
   * Ajax call to get all cart data json.
   */
  function getCartJson() {
    axios
      .get(`/cart.js?no-cache}`)
      .then((response) => {
        Karmoon.EventBus.emit('Cart:updated', response.data);
      })
      .catch((error) => {
        window.console.error(error);
        return error;
      });
  }

  /**
   * Handle quantity updates.
   * @param {HTMLElement} element - Quantity selector node element.
   */
  function handleQuantityEvent(element) {
    Karmoon.EventBus.emit('Cart:isUpdating');

    const data = {
      index: element.closest(selectors.lineItem).dataset.index,
      key: element.closest(selectors.lineItem).dataset.key,
      quantity: parseInt(element.querySelector('input').value, 10),
    };

    cart
      .updateItem(data.key, { quantity: data.quantity })
      .then(() => {
        Karmoon.EventBus.emit('Cart:itemAdded');
      })
      .catch((error) => {
        if (typeof response === 'undefined') {
          return;
        }

        throw new Error('Could not update item data', error);
      });
  }

  /**
   * Instantiate quantity selectors using id.
   * - Callback has a timer to reduce number of calls.
   */
  function constructQuantitySelectors() {
    const quantitySelectors = document.querySelectorAll(
      selectors.quantitySelector
    );

    [...quantitySelectors].forEach((item) => {
      quantitySelector(`${item.id}`, () => {
        window.clearTimeout(window.timer);
        window.timer = window.setTimeout(() => {
          handleQuantityEvent(item);
        }, 250);
      }).init();
    });
  }

  /**
   * Build cart template
   * @param {Object} data - Cart data.
   */
  function cartRender(data) {
    const cartItems = data.items;

    if (cartItems.length) {
      const cartTemplate = getCartTemplate(data);
      nodeSelectors.itemList.innerHTML = cartTemplate;
    }

    constructQuantitySelectors();
  }

  /**
   * Get cart template
   * @param {Object} data - Cart data.
   * @returns {String}
   */
  function getCartTemplate(data) {
    const lineItemsTemplate = data.items
      .map((item, index) => getLineItemTemplate(item, index))
      .join('');

    return `${lineItemsTemplate}`;
  }

  /**
   * Return line item HTML block.
   * @param {Object} item - Cart item.
   * @param {interger} index - Index count.
   * @returns {HTMLElement}
   */
  function getLineItemTemplate(item, index) {
    const cartVariantTitle = getVariantTitle(item);
    const cartLinePrice = getLinePrice(item);
    const createQuantitySelector = quantitySelectorTemplate(item, index);
    const preOrder = getPreOrder(item);
    const preOrderHTML = getPreOrderHTML(preOrder);


    const cartRow = `
    <div
    class="cart__row grid grid-cols-7 items-start border-b border-grey py-10px my-10px"
    data-key="${item.key}"
    data-index="${index}"
    js-cart-table="lineItem"
  >
    <div class="cart__image col-span-3 md:col-span-1">
      <a href="${item.url}" title="${item.product_title}">
        <div class="img--square">
          <img loading="lazy" src="${item.image}" />
        </div>
      </a>
    </div>

    <div class="cart__content flex-col justify-between md:justify-start md:h-auto col-span-4 md:col-span-6 grid grid-cols-1 md:grid-cols-6">
      <div class="col-span-3 flex flex-col pl-20px">
        <a href="${item.url}" title="${item.product_title}">
          <h5 class="cart-item__title leading-tight font-light text-blue text-lg m-0 p-0">${item.product_title}</h5>
        </a>

        ${cartVariantTitle}

        ${preOrderHTML}

        <button
          class="p-0 m-0 text-sm text-blue font-light self-start"
          js-cart="remove"
          data-item-key="${item.key}"
          name="Remove"
          type="button"
          tabindex="0">
            x Remove
        </button>
      </div>

      <div class="cart-item__price md:flex col-span-1 hidden">
        <span class="cart-item__detail-title hidden">{{ 'cart.label.price' | t }}</span>
        <span class="text-lg">${cartLinePrice}</span>
      </div>

      <div class="col-span-1 md:flex justify-center items-center pl-10px md:pl-0">
        ${createQuantitySelector}
      </div>

      <div class="cart-item__subtotal md:flex col-span-1 text-lg items-end justify-items-end">
        <span class="cart-item__detail-title hidden">{{ 'cart.label.subtotal' | t }}</span>
        <span class="text-base md:text-lg text-right w-full">${cartLinePrice}</span>
      </div>
    </div>
  </div>`;

    return cartRow;
  }

  /**
   * Return Variant title if avaible.
   * @param {Object} item - Cart item.
   * @returns {HTMLElement}
   */
  function getVariantTitle(item) {
    return item.variant_title === null
      ? ''
      : `<p class="cart-item__variant leading-none">${item.variant_title}</p>`;
  }

  /**
   * Return Properties of Pre-Order if available.
   * @param {Object} item - Cart item.
   * @returns {HTMLElement}
   */
  function getPreOrder(item) {
    if (item.properties) {
      if (item.properties.hasOwnProperty('Pre-Order')) {
        return 'Pre-Order';
      } else {
        return '';
      }
    } else {
      return '';
    }
  }

  /**
   * Return Properties of Pre-Order if available.
   * @param {String} item - 'Pre-Order' or ''.
   * @returns {HTMLElement}
   */
  function getPreOrderHTML(item) {
    return item == 'Pre-Order'
      ? `<p class="cart-item__variant leading-none">${item}</p>`
      : ``;
  }

  /**
   * Return formatted line item price.
   * @param {Object} item - Cart item.
   * @returns {HTMLElement}
   */
  function getLinePrice(item) {
    const cartLinePrice = formatMoney(
      item.line_price,
      theme.moneyFormat
    ).replace('.00', '');

    return cartLinePrice;
  }

  /**
   * Update total items in cart counter.
   * @param {Object} data - Cart data.
   */
  function cartCounter(data) {
    [...nodeSelectors.counter].forEach((item) => {
      item.innerHTML = data.item_count;

      if (data.item_count === 0) {
        item.classList.remove(cssClasses.active);
      } else {
        item.classList.add(cssClasses.active);
      }
    });
  }

  /**
   * Update total cart price.
   * @param {Object} data - Cart data.
   */
  function updateTotal(data) {
    const cartTotal = formatMoney(data.total_price, theme.moneyFormat).replace(
      '.00',
      ''
    );

    [...nodeSelectors.totalPrice].forEach((item) => {
      item.innerHTML = `<span class=money>${cartTotal}</span>`;
    });
  }

  /**
   * Add loading state when adding to cart.
   */
  function cartUpdating() {
    nodeSelectors.container.classList.add(cssClasses.loading);
  }

  /**
   * Remove loading state from Ajax cart.
   */
  function cartUpdated() {
    nodeSelectors.container.classList.remove(cssClasses.loading);
  }

  /**
   * Return immutable object.
   */
  return Object.freeze({
    init,
  });
};
